// H-003 — pestaña «Historial de cambios» de la ficha: elige un registro del paciente (sus datos de filiación o uno de sus registros clínicos recientes) y muestra
// su historial de versiones (HistorialVersiones). Es OTRA cosa que la Auditoría (admin/AuditoriaPage.tsx, quién ABRIÓ qué): aquí se ve quién MODIFICÓ un registro y cuándo.
// Solo lectura. Cada búsqueda va con la sesión del usuario por el proxy: lo que su rol (AccessPolicy) o la regla de vínculo no le dejan ver no aparece en la lista.
import { useMedplum } from '@medplum/react-hooks';
import type { Resource, ResourceType } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router';
import { useEspecialidadDelPracticante } from '@/lib/useEspecialidadDelPracticante';
import { esDirectorTecnicoFarmacia, useRolDelPracticante } from '@/lib/useRolDelPracticante';
import { TablaVacia } from './TablaVacia';
import { HistorialVersiones } from './HistorialVersiones';

/** Tipos clínicos del paciente cuyo historial se ofrece (los que el HCE edita). Todos aceptan `patient=` en la búsqueda. */
export const TIPOS_CON_HISTORIAL: readonly ResourceType[] = ['QuestionnaireResponse', 'Condition', 'AllergyIntolerance', 'MedicationRequest', 'DocumentReference'];
const POR_TIPO = 20;

interface Registro {
  tipo: ResourceType;
  id: string;
  descripcion: string | null;
  actualizado: string | null;
}

/** Texto corto que identifica el registro en la lista (lo mismo que ya muestra su pestaña), sin abrir su contenido. */
export function descripcionDeRegistro(r: Resource): string | null {
  switch (r.resourceType) {
    case 'Condition':
    case 'AllergyIntolerance':
      return r.code?.text ?? r.code?.coding?.[0]?.display ?? null;
    case 'MedicationRequest':
      return r.medicationCodeableConcept?.text ?? r.medicationCodeableConcept?.coding?.[0]?.display ?? r.medicationReference?.display ?? null;
    case 'DocumentReference':
      return r.description ?? r.type?.text ?? null;
    case 'QuestionnaireResponse':
      return r.questionnaire?.split('/').pop()?.split('|')[0] || null;
    default:
      return null;
  }
}

/**
 * Enfermería y el Director Técnico Q.F./B.F. no tienen esta pestaña en su lista reducida: si llegan por URL directa se los devuelve a la primera pestaña de la
 * suya (mismo criterio que PatientChartPage). Es coherencia de la interfaz: el servidor sigue filtrando lo que cada rol puede leer. Mientras el rol o la
 * especialidad cargan no se muestra nada (ni se pide el historial).
 */
export function HistorialCambiosTab(): JSX.Element {
  const { id } = useParams() as { id: string };
  const rol = useRolDelPracticante();
  const especialidad = useEspecialidadDelPracticante();
  if (esDirectorTecnicoFarmacia(rol)) return <Navigate to={`/${id}/medicamentos`} replace />;
  if (especialidad.cargando) return <></>;
  if (especialidad.codigo === 'enfermeria') return <Navigate to={`/${id}/signos-vitales`} replace />;
  return <HistorialCambiosContenido />;
}

function HistorialCambiosContenido(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const [registros, setRegistros] = useState<Registro[] | null>(null);
  const [elegido, setElegido] = useState<{ tipo: ResourceType; id: string }>({ tipo: 'Patient', id });

  useEffect(() => {
    let vigente = true;
    setRegistros(null);
    setElegido({ tipo: 'Patient', id });
    Promise.all(
      TIPOS_CON_HISTORIAL.map((tipo) =>
        medplum
          .searchResources(tipo, { patient: `Patient/${id}`, _sort: '-_lastUpdated', _count: POR_TIPO })
          .then((lista) => (lista as Resource[]).filter((r) => r.id).map((r): Registro => ({ tipo, id: r.id!, descripcion: descripcionDeRegistro(r), actualizado: r.meta?.lastUpdated ?? null })))
          // Un tipo que el rol no puede leer (403 de la AccessPolicy) simplemente no se ofrece.
          .catch(() => [] as Registro[]),
      ),
    ).then((porTipo) => {
      if (!vigente) return;
      const todos = porTipo.flat().sort((a, b) => (b.actualizado ?? '').localeCompare(a.actualizado ?? ''));
      setRegistros(todos);
    });
    return () => {
      vigente = false;
    };
  }, [medplum, id]);

  const clave = (tipo: string, rid: string): string => `${tipo}/${rid}`;
  const opciones: Registro[] = [{ tipo: 'Patient', id, descripcion: null, actualizado: null }, ...(registros ?? [])];

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h5 className="text-sm font-semibold">{t('fichaPaciente.tabs.historialCambios')}</h5>
        <p className="text-xs text-muted-foreground">{t('historialCambios.explicacion')}</p>
      </div>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">{t('historialCambios.registro')}</span>
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={clave(elegido.tipo, elegido.id)}
          onChange={(e) => {
            const [tipo, rid] = e.target.value.split('/') as [ResourceType, string];
            setElegido({ tipo, id: rid });
          }}
        >
          {opciones.map((o) => (
            <option key={clave(o.tipo, o.id)} value={clave(o.tipo, o.id)}>
              {o.tipo === 'Patient'
                ? t('historialCambios.datosDelPaciente')
                : [t(`historialCambios.tipos.${o.tipo}`, o.tipo), o.descripcion, o.actualizado ? new Date(o.actualizado).toLocaleDateString('es-EC', { timeZone: 'America/Guayaquil' }) : null]
                    .filter(Boolean)
                    .join(' · ')}
            </option>
          ))}
        </select>
      </label>
      {registros === null && <p className="text-xs text-muted-foreground">{t('historialCambios.cargandoRegistros')}</p>}
      {registros !== null && registros.length === 0 && <TablaVacia mensaje={t('historialCambios.sinRegistrosClinicos')} />}
      <HistorialVersiones key={clave(elegido.tipo, elegido.id)} tipo={elegido.tipo} id={elegido.id} />
    </div>
  );
}
